import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { listAnbiyam }              from '@/api/anbiyam';
import type { Anbiyam }             from '@/api/anbiyam';
import { getFamily, createFamily, updateFamily } from '@/api/family';
import type { FamilyPayload } from '@/api/family';
import { createFamilyMember, updateFamilyMember } from '@/api/familyMember';
import type { FamilyMemberPayload } from '@/api/familyMember';
import { validatePhone, validateEmail, validateZipcode } from '@/utils/validation';

function FieldError({ msg }: { msg: string }) {
  return msg ? <p className="text-red-500 text-xs mt-0.5">{msg}</p> : null;
}

const pickStr = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row) return '';
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return '';
};

const pickBool = (row: Record<string, unknown> | undefined, keys: string[]): boolean => {
  if (!row) return false;
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return v === true || v === 1 || v === '1' || v === 'true';
  }
  return false;
};

const pickNum = (row: Record<string, unknown> | undefined, keys: string[]): number => {
  if (!row) return 0;
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return Number(v) || 0;
  }
  return 0;
};

const pickDate = (row: Record<string, unknown> | undefined, keys: string[]): string | null => {
  const v = pickStr(row, keys);
  return v ? v.slice(0, 10) : null;
};

/**
 * Head of Family / Spouse are edited on this page, but the rest of the app
 * (FamilyDetailPage, member lists, certificates) displays them from their
 * dbo.family_member rows, not from the family table's head_of_family/email
 * columns that sp_SaveFamily writes. Build a full FamilyMemberPayload from
 * the existing member row (preserving gender/dob/occupation/activities/etc.)
 * with just name/phone/email/relationship overridden, so saving here keeps
 * the actual displayed record in sync instead of silently going nowhere.
 */
const buildMemberPayload = (
  existing: Record<string, unknown> | undefined,
  overrides: { name: string; phone: string; email: string; relationship: string },
): FamilyMemberPayload => ({
  name:         overrides.name,
  relationship: overrides.relationship,
  phone:        overrides.phone,
  email:        overrides.email,
  gender:             pickStr(existing, ['gender', 'Gender']) || (overrides.relationship === 'Spouse' ? 'Female' : 'Male'),
  dob:                pickDate(existing, ['dob', 'DOB']),
  status:             pickStr(existing, ['status', 'member_status', 'Status']) || 'Active',
  occupation:         pickStr(existing, ['occupation', 'Occupation']),
  qualification:      pickStr(existing, ['qualification', 'Qualification']),
  bloodGroup:         pickStr(existing, ['bloodGroup', 'BloodGroup', 'blood_group']),
  baptizedDate:       pickDate(existing, ['baptismDate', 'BaptismDate']),
  marriageDate:       pickDate(existing, ['marriageDate', 'MarriageDate']),
  confirmationDate:   pickDate(existing, ['confirmationDate', 'confirmation']),
  firstCommunionDate: pickDate(existing, ['communionDate', 'Communion']),
  priesthoodDate:     pickDate(existing, ['priesthoodDate', 'Priesthood']),
  memberGroup:        pickStr(existing, ['memberGroup', 'MemberGroup']),
  childClass:         pickStr(existing, ['childClass', 'ChildClass']),
  childInstitution:   pickStr(existing, ['childInstitution', 'ChildInstitution']),
  isAdmin:            pickBool(existing, ['isAdmin', 'isadmin']),
  legionOfMary:       pickBool(existing, ['isLOM', 'legionofmary']),
  isVincentDePaul:    pickBool(existing, ['isVDPaul', 'isvencentdepaul']),
  isChoir:            pickBool(existing, ['isChoir', 'ischoir']),
  isCatechismTeacher: pickBool(existing, ['isCatechismTeacher', 'iscatechismteacher']),
  isWomenAssoc:       pickBool(existing, ['isWomenAssoc', 'iswomenassoc']),
  isLiturgyCouncil:   pickBool(existing, ['isLitergyCouncil', 'islitergycouncil']),
  isAlterServices:    pickBool(existing, ['isAlterServices', 'isalterservices']),
  isYouth:            pickBool(existing, ['isYouth', 'isyouth']),
});

export default function FamilyFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [anbiyamOptions, setAnbiyamOptions] = useState<Anbiyam[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);

  // Per-field errors
  const [errs, setErrs] = useState<Record<string, string>>({});
  const setFieldErr = (field: string, msg: string) =>
    setErrs((prev) => ({ ...prev, [field]: msg }));
  const clearFieldErr = (field: string) => setFieldErr(field, '');

  // Form state
  const [familyCode,           setFamilyCode]           = useState('');
  const [anbiyamId,            setAnbiyamId]            = useState<number>(0);
  const [permanentAddress,     setPermanentAddress]     = useState('');
  const [permCity,             setPermCity]             = useState('');
  const [permState,            setPermState]            = useState('');
  const [permZipcode,          setPermZipcode]          = useState('');
  const [tempAddress,          setTempAddress]          = useState('');
  const [tempCity,             setTempCity]             = useState('');
  const [tempState,            setTempState]            = useState('');
  const [tempZipcode,          setTempZipcode]          = useState('');
  const [sameAsPermanent,      setSameAsPermanent]      = useState(false);
  const [monthlySubscription,  setMonthlySubscription]  = useState<number>(0);
  const [isMultipleCards,      setIsMultipleCards]      = useState(false);
  const [familyNotes,          setFamilyNotes]          = useState('');

  const [headName,  setHeadName]  = useState('');
  const [headPhone, setHeadPhone] = useState('');
  const [headEmail, setHeadEmail] = useState('');
  const [isHusbandActive, setIsHusbandActive] = useState(true);

  const [spouseName,  setSpouseName]  = useState('');
  const [spousePhone, setSpousePhone] = useState('');
  const [spouseEmail, setSpouseEmail] = useState('');
  const [isWifeActive, setIsWifeActive] = useState(true);

  // Full underlying dbo.family_member rows for Head/Spouse (when editing) —
  // kept so saving can preserve fields not shown on this form (gender, dob,
  // occupation, activities, etc.) while updating name/phone/email here.
  const [headMember,   setHeadMember]   = useState<Record<string, unknown> | undefined>(undefined);
  const [spouseMember, setSpouseMember] = useState<Record<string, unknown> | undefined>(undefined);

  // Keep Temporary address mirrored to Permanent while "Same as Permanent" is checked
  useEffect(() => {
    if (!sameAsPermanent) return;
    setTempAddress(permanentAddress);
    setTempCity(permCity);
    setTempState(permState);
    setTempZipcode(permZipcode);
  }, [sameAsPermanent, permanentAddress, permCity, permState, permZipcode]);

  // Load Anbiyam options for the dropdown
  useEffect(() => {
    listAnbiyam({ page: 1, pageSize: 50 })
      .then((r) => setAnbiyamOptions(r.items))
      .catch(() => {});
  }, []);

  // Load existing family when editing
  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    getFamily(parseInt(id, 10))
      .then(({ family, members }) => {
        setFamilyCode(pickStr(family, ['familyCode', 'FamilyCode', 'family_code']));
        setAnbiyamId(pickNum(family, ['anbiyamId', 'AnbiyamId', 'anbiyam_id']));
        setPermanentAddress(pickStr(family, ['permanentAddress', 'family_permanant_address']));
        setPermCity(pickStr(family, ['permCity', 'family_perm_city']));
        setPermState(pickStr(family, ['permState', 'family_perm_state']));
        setPermZipcode(pickStr(family, ['permZipcode', 'family_perm_zipcode']));
        setTempAddress(pickStr(family, ['tempAddress', 'family_temp_address']));
        setTempCity(pickStr(family, ['tempCity', 'family_temp_city']));
        setTempState(pickStr(family, ['tempState', 'family_temp_state']));
        setTempZipcode(pickStr(family, ['tempZipcode', 'family_temp_zipcode']));
        setMonthlySubscription(pickNum(family, ['monthlySubscription', 'monthly_subscription']));
        setIsMultipleCards(pickBool(family, ['isMultipleCards', 'is_multiple_cards']));
        setFamilyNotes(pickStr(family, ['familyNotes', 'family_notes']));

        const head   = members.find((m) => /head/i.test(pickStr(m, ['relationship', 'Relationship'])));
        const spouse = members.find((m) => /spouse|wife|husband/i.test(pickStr(m, ['relationship', 'Relationship'])));
        setHeadMember(head);
        setSpouseMember(spouse);
        if (head)   { setHeadName(pickStr(head, ['name', 'Name']));     setHeadPhone(pickStr(head, ['phone', 'Phone']));     setHeadEmail(pickStr(head, ['email', 'Email'])); }
        if (spouse) { setSpouseName(pickStr(spouse, ['name', 'Name'])); setSpousePhone(pickStr(spouse, ['phone', 'Phone'])); setSpouseEmail(pickStr(spouse, ['email', 'Email'])); }
        setIsHusbandActive(pickBool(family, ['isHusbandActive', 'ishusbandactive']) || true);
        setIsWifeActive(pickBool(family, ['isWifeActive', 'iswifeactive']) || true);
      })
      .catch(() => setError('Failed to load family for editing.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate all phone/email/zipcode fields
    const fieldValidations: Record<string, string> = {
      headPhone:    validatePhone(headPhone),
      headEmail:    validateEmail(headEmail),
      spousePhone:  validatePhone(spousePhone),
      spouseEmail:  validateEmail(spouseEmail),
      permZipcode:  validateZipcode(permZipcode),
      tempZipcode:  validateZipcode(tempZipcode),
    };
    setErrs(fieldValidations);
    const hasFieldErr = Object.values(fieldValidations).some(Boolean);

    if (!isEdit && !familyCode.trim()) { setError('Family code is required'); return; }
    if (!anbiyamId)                    { setError('Anbiyam is required');     return; }
    if (!headName.trim())              { setError('Head of family name is required'); return; }
    if (hasFieldErr)                   { setError('Please fix the highlighted fields before saving.'); return; }

    const payload: FamilyPayload = {
      familyCode: familyCode.trim(),
      anbiyamId,
      permanentAddress, permCity, permState, permZipcode,
      tempAddress, tempCity, tempState, tempZipcode,
      monthlySubscription, isMultipleCards, familyNotes,
      headOfFamily: { name: headName.trim(), phone: headPhone, email: headEmail },
      ...(spouseName.trim() ? { spouse: { name: spouseName.trim(), phone: spousePhone, email: spouseEmail } } : {}),
      isHusbandActive, isWifeActive,
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        const familyId = parseInt(id, 10);
        await updateFamily(familyId, payload);

        // sp_SaveFamily only updates the family table's own head_of_family/
        // email/phone columns — it does NOT touch the dbo.family_member rows
        // that FamilyDetailPage and every other screen actually display.
        // Sync those rows here so the edit is visible, not just stored.
        const headMemberId = pickNum(headMember, ['memberId', 'member_id', 'MemberId', 'MemberID']);
        const headPayload = buildMemberPayload(headMember, {
          name: headName.trim(), phone: headPhone, email: headEmail,
          relationship: pickStr(headMember, ['relationship', 'Relationship']) || 'Head',
        });
        if (headMemberId) await updateFamilyMember(familyId, headMemberId, headPayload);
        else if (headName.trim()) await createFamilyMember(familyId, headPayload);

        if (spouseName.trim()) {
          const spouseMemberId = pickNum(spouseMember, ['memberId', 'member_id', 'MemberId', 'MemberID']);
          const spousePayload = buildMemberPayload(spouseMember, {
            name: spouseName.trim(), phone: spousePhone, email: spouseEmail,
            relationship: pickStr(spouseMember, ['relationship', 'Relationship']) || 'Spouse',
          });
          if (spouseMemberId) await updateFamilyMember(familyId, spouseMemberId, spousePayload);
          else await createFamilyMember(familyId, spousePayload);
        }

        navigate(`/families/${id}`);
      } else {
        const result = await createFamily(payload);
        const familyId = result.familyId;

        // sp_SaveFamily only stores head_of_family on the family table itself;
        // it does not create dbo.family_member rows (spouse isn't passed to it
        // at all — see backend/family/create.js). Every other screen
        // (FamilyDetailPage, member lists, certificates) reads Head/Spouse from
        // family_member, so create those rows here or they show as "No record".
        const headPayload = buildMemberPayload(undefined, {
          name: headName.trim(), phone: headPhone, email: headEmail, relationship: 'Head',
        });
        await createFamilyMember(familyId, headPayload);

        if (spouseName.trim()) {
          const spousePayload = buildMemberPayload(undefined, {
            name: spouseName.trim(), phone: spousePhone, email: spouseEmail, relationship: 'Spouse',
          });
          await createFamilyMember(familyId, spousePayload);
        }

        navigate(`/families/${familyId}`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-teal text-sm">Loading…</div>;

  return (
    <div className="p-6 max-w-4xl">
      <Link to={isEdit ? `/families/${id}` : '/families'} className="text-teal text-xs hover:underline">
        &larr; Back
      </Link>
      <h1 className="text-xl font-semibold text-navy font-georgia mt-1 mb-6">
        {isEdit ? 'Edit Family' : 'Add Family'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-4 py-2">
            {error}
          </div>
        )}

        {/* Basic info */}
        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Family Code {isEdit && <span className="text-gray-400 text-xs">(read-only)</span>}</label>
              <input
                type="text" className="input-field" value={familyCode}
                onChange={(e) => setFamilyCode(e.target.value)}
                readOnly={isEdit} disabled={isEdit} required={!isEdit}
              />
            </div>
            <div>
              <label className="label">Anbiyam</label>
              <select
                className="input-field" value={anbiyamId}
                onChange={(e) => setAnbiyamId(Number(e.target.value))} required
              >
                <option value={0}>Select Anbiyam…</option>
                {anbiyamOptions.map((a) => (
                  <option key={a.anbiyamId} value={a.anbiyamId}>{a.anbiyamName} (Zone {a.zone})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Monthly Subscription (₹)</label>
              <input
                type="number" min={0} step="0.01" className="input-field"
                value={monthlySubscription}
                onChange={(e) => setMonthlySubscription(Number(e.target.value))}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox" id="multipleCards" checked={isMultipleCards}
                onChange={(e) => setIsMultipleCards(e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="multipleCards" className="text-sm text-navy">Has multiple subscription cards</label>
            </div>
            <div className="md:col-span-2">
              <label className="label">Family Notes</label>
              <textarea
                className="input-field" rows={2} value={familyNotes}
                onChange={(e) => setFamilyNotes(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Addresses */}
        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Addresses</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-teal uppercase tracking-wide">Permanent</p>
              <div><label className="label">Address</label>
                <textarea className="input-field" rows={2} value={permanentAddress} onChange={(e) => setPermanentAddress(e.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input className="input-field" placeholder="City"    value={permCity}    onChange={(e) => setPermCity(e.target.value)} />
                <input className="input-field" placeholder="State"   value={permState}   onChange={(e) => setPermState(e.target.value)} />
                <div>
                  <input
                    className={`input-field w-full ${errs.permZipcode ? 'border-red-400' : ''}`}
                    placeholder="Zipcode"
                    value={permZipcode}
                    onChange={(e) => { setPermZipcode(e.target.value); if (errs.permZipcode) setFieldErr('permZipcode', validateZipcode(e.target.value)); }}
                    onBlur={(e) => setFieldErr('permZipcode', validateZipcode(e.target.value))}
                    maxLength={6}
                  />
                  <FieldError msg={errs.permZipcode ?? ''} />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-teal uppercase tracking-wide">Temporary</p>
                <label className="flex items-center gap-1.5 text-xs text-navy">
                  <input
                    type="checkbox" className="h-3.5 w-3.5" checked={sameAsPermanent}
                    onChange={(e) => setSameAsPermanent(e.target.checked)}
                  />
                  Same as Permanent Address
                </label>
              </div>
              <div><label className="label">Address</label>
                <textarea
                  className="input-field disabled:bg-gray-100 disabled:text-gray-500" rows={2}
                  value={tempAddress} disabled={sameAsPermanent}
                  onChange={(e) => setTempAddress(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  className="input-field disabled:bg-gray-100 disabled:text-gray-500" placeholder="City"
                  value={tempCity} disabled={sameAsPermanent} onChange={(e) => setTempCity(e.target.value)}
                />
                <input
                  className="input-field disabled:bg-gray-100 disabled:text-gray-500" placeholder="State"
                  value={tempState} disabled={sameAsPermanent} onChange={(e) => setTempState(e.target.value)}
                />
                <div>
                  <input
                    className={`input-field w-full disabled:bg-gray-100 disabled:text-gray-500 ${errs.tempZipcode ? 'border-red-400' : ''}`}
                    placeholder="Zipcode"
                    value={tempZipcode} disabled={sameAsPermanent}
                    onChange={(e) => { setTempZipcode(e.target.value); if (errs.tempZipcode) setFieldErr('tempZipcode', validateZipcode(e.target.value)); }}
                    onBlur={(e) => setFieldErr('tempZipcode', validateZipcode(e.target.value))}
                    maxLength={6}
                  />
                  <FieldError msg={errs.tempZipcode ?? ''} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Head & Spouse */}
        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Head of Family &amp; Spouse</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-teal uppercase tracking-wide">Head of Family</p>
              <div><label className="label">Name</label>
                <input className="input-field" value={headName} onChange={(e) => setHeadName(e.target.value)} required />
              </div>
              <div><label className="label">Phone</label>
                <input
                  className={`input-field ${errs.headPhone ? 'border-red-400' : ''}`}
                  value={headPhone}
                  maxLength={10}
                  onChange={(e) => { setHeadPhone(e.target.value); if (errs.headPhone) setFieldErr('headPhone', validatePhone(e.target.value)); }}
                  onBlur={(e) => setFieldErr('headPhone', validatePhone(e.target.value))}
                  placeholder="10-digit mobile"
                />
                <FieldError msg={errs.headPhone ?? ''} />
              </div>
              <div><label className="label">Email</label>
                <input
                  type="text"
                  className={`input-field ${errs.headEmail ? 'border-red-400' : ''}`}
                  value={headEmail}
                  onChange={(e) => { setHeadEmail(e.target.value); if (errs.headEmail) setFieldErr('headEmail', validateEmail(e.target.value)); }}
                  onBlur={(e) => setFieldErr('headEmail', validateEmail(e.target.value))}
                  placeholder="user@example.com"
                />
                <FieldError msg={errs.headEmail ?? ''} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="husbandActive" checked={isHusbandActive} onChange={(e) => setIsHusbandActive(e.target.checked)} className="h-4 w-4" />
                <label htmlFor="husbandActive" className="text-sm text-navy">Active</label>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-teal uppercase tracking-wide">Spouse (optional)</p>
              <div><label className="label">Name</label>
                <input className="input-field" value={spouseName} onChange={(e) => setSpouseName(e.target.value)} />
              </div>
              <div><label className="label">Phone</label>
                <input
                  className={`input-field ${errs.spousePhone ? 'border-red-400' : ''}`}
                  value={spousePhone}
                  maxLength={10}
                  onChange={(e) => { setSpousePhone(e.target.value); if (errs.spousePhone) setFieldErr('spousePhone', validatePhone(e.target.value)); }}
                  onBlur={(e) => setFieldErr('spousePhone', validatePhone(e.target.value))}
                  placeholder="10-digit mobile"
                />
                <FieldError msg={errs.spousePhone ?? ''} />
              </div>
              <div><label className="label">Email</label>
                <input
                  type="text"
                  className={`input-field ${errs.spouseEmail ? 'border-red-400' : ''}`}
                  value={spouseEmail}
                  onChange={(e) => { setSpouseEmail(e.target.value); if (errs.spouseEmail) setFieldErr('spouseEmail', validateEmail(e.target.value)); }}
                  onBlur={(e) => setFieldErr('spouseEmail', validateEmail(e.target.value))}
                  placeholder="user@example.com"
                />
                <FieldError msg={errs.spouseEmail ?? ''} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="wifeActive" checked={isWifeActive} onChange={(e) => setIsWifeActive(e.target.checked)} className="h-4 w-4" />
                <label htmlFor="wifeActive" className="text-sm text-navy">Active</label>
              </div>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Update Family' : 'Create Family'}
          </button>
        </div>
      </form>
    </div>
  );
}
